/**
 * ModalProgramsManagement.js
 *
 * Modal for managing Programs - create, edit, delete and assign scenarios
 */

import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Box,
  Typography,
  makeStyles,
  IconButton,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Popover,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import RemoveCircleOutlineIcon from "@material-ui/icons/RemoveCircleOutline";
import { ThreeDots } from "react-loader-spinner";
import { toast } from "react-toastify";
import AuthContext from "../../../../connections/auth/AuthProvider";
import {
  getPrograms,
  getProgramDetail,
  createProgram,
  updateProgram,
  deleteProgram,
  getAvailableScenarios,
  addScenarioToProgram,
  removeScenarioFromProgram,
} from "../../viewServices/apiCalls";

const useStyles = makeStyles((theme) => ({
  modalContainer: {
    display: "flex",
    flexDirection: "column",
    height: "80vh",
    maxHeight: "700px",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: "900px",
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[24],
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden",
  },
  titleSection: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: "#f5f5f5",
    padding: theme.spacing(1.5, 2),
  },
  titleLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  title: {
    fontSize: "1.1rem",
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  closeButton: {
    padding: theme.spacing(0.5),
  },
  contentSection: {
    flex: 1,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  // List View
  listHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  programsList: {
    flex: 1,
    overflow: "auto",
    padding: 0,
  },
  programItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  programItemContent: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  },
  programName: {
    fontWeight: 500,
    fontSize: "0.95rem",
  },
  programCounts: {
    display: "flex",
    gap: theme.spacing(0.5),
  },
  countChip: {
    height: 20,
    fontSize: "0.7rem",
    "& .MuiChip-label": {
      padding: "0 8px",
    },
  },
  // Edit View
  editSection: {
    flex: 1,
    overflow: "auto",
    padding: theme.spacing(2),
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  scenariosSection: {
    marginTop: theme.spacing(2),
  },
  sectionTitle: {
    fontSize: "0.95rem",
    fontWeight: 500,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  tabsContainer: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tabContent: {
    padding: theme.spacing(2, 0),
  },
  scenarioRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  scenarioName: {
    fontSize: "0.875rem",
  },
  addScenarioRow: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    marginTop: theme.spacing(1),
  },
  selectScenario: {
    flex: 1,
    minWidth: 200,
  },
  emptyMessage: {
    color: theme.palette.text.secondary,
    fontStyle: "italic",
    padding: theme.spacing(2),
    textAlign: "center",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    minHeight: 200,
  },
  actionsFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: "#fafafa",
  },
  popover: {
    padding: theme.spacing(2),
  },
}));

// Tab Panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

export default function ModalProgramsManagement({
  open,
  onClose,
  onProgramsChange,
}) {
  const classes = useStyles();
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails?.implementation_color || "#1976d2";

  // View state: 'list' or 'edit'
  const [view, setView] = useState("list");

  // Programs list
  const [programs, setPrograms] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Edit state
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [programDetail, setProgramDetail] = useState(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Form fields
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formStatus, setFormStatus] = useState("active");

  // Scenarios
  const [availableScenarios, setAvailableScenarios] = useState([]);
  const [selectedScenarioToAdd, setSelectedScenarioToAdd] = useState("");
  const [activeTab, setActiveTab] = useState(0);

  // Delete confirmation
  const [deleteAnchor, setDeleteAnchor] = useState(null);
  const [programToDelete, setProgramToDelete] = useState(null);

  // Fetch programs list
  const fetchPrograms = async () => {
    try {
      setIsLoading(true);
      const response = await getPrograms();
      setPrograms(response.data || []);
    } catch (error) {
      toast.error("Failed to load programs");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch program detail
  const fetchProgramDetail = async (programId) => {
    try {
      setIsLoadingDetail(true);
      const [detailResponse, availableResponse] = await Promise.all([
        getProgramDetail(programId),
        getAvailableScenarios(programId),
      ]);
      setProgramDetail(detailResponse.data);
      setAvailableScenarios(availableResponse.data || []);

      // Set form fields
      setFormName(detailResponse.data.program_name || "");
      setFormDescription(detailResponse.data.program_description || "");
      setFormStatus(detailResponse.data.program_status || "active");
    } catch (error) {
      toast.error("Failed to load program details");
    } finally {
      setIsLoadingDetail(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (open) {
      fetchPrograms();
      setView("list");
      setSelectedProgram(null);
      setProgramDetail(null);
    }
  }, [open]);

  // Handle edit program
  const handleEditProgram = (program) => {
    setSelectedProgram(program);
    setIsCreating(false);
    setView("edit");
    fetchProgramDetail(program.program_id);
  };

  // Handle create new program
  const handleCreateNew = () => {
    setSelectedProgram(null);
    setProgramDetail(null);
    setIsCreating(true);
    setFormName("");
    setFormDescription("");
    setFormStatus("active");
    setAvailableScenarios([]);
    setView("edit");
  };

  // Handle back to list
  const handleBackToList = () => {
    setView("list");
    setSelectedProgram(null);
    setProgramDetail(null);
    setIsCreating(false);
    fetchPrograms();
  };

  // Handle save program
  const handleSaveProgram = async () => {
    if (!formName.trim()) {
      toast.error("Program name is required");
      return;
    }

    try {
      if (isCreating) {
        const response = await createProgram({
          program_name: formName.trim(),
          program_description: formDescription.trim(),
          program_status: formStatus,
        });
        toast.success("Program created successfully");

        // Switch to edit mode for the new program
        setIsCreating(false);
        setSelectedProgram({ program_id: response.program_id });
        fetchProgramDetail(response.program_id);
      } else {
        await updateProgram(selectedProgram.program_id, {
          program_name: formName.trim(),
          program_description: formDescription.trim(),
          program_status: formStatus,
        });
        toast.success("Program updated successfully");
        fetchProgramDetail(selectedProgram.program_id);
      }

      if (onProgramsChange) onProgramsChange();
    } catch (error) {
      toast.error(
        isCreating ? "Failed to create program" : "Failed to update program",
      );
    }
  };

  // Handle delete program
  const handleDeleteClick = (event, program) => {
    event.stopPropagation();
    setDeleteAnchor(event.currentTarget);
    setProgramToDelete(program);
  };

  const handleDeleteConfirm = async () => {
    if (!programToDelete) return;

    try {
      await deleteProgram(programToDelete.program_id);
      toast.success("Program deleted successfully");
      setDeleteAnchor(null);
      setProgramToDelete(null);
      fetchPrograms();
      if (onProgramsChange) onProgramsChange();
    } catch (error) {
      toast.error("Failed to delete program");
    }
  };

  const handleDeleteCancel = () => {
    setDeleteAnchor(null);
    setProgramToDelete(null);
  };

  // Handle add scenario
  const handleAddScenario = async () => {
    if (!selectedScenarioToAdd || !selectedProgram) return;

    const scenarioTypes = ["actual", "forecast", "policy"];
    const scenarioType = scenarioTypes[activeTab];

    try {
      await addScenarioToProgram(selectedProgram.program_id, {
        scenario_id: selectedScenarioToAdd,
        scenario_type: scenarioType,
        display_order: 0,
      });
      toast.success("Scenario added successfully");
      setSelectedScenarioToAdd("");
      fetchProgramDetail(selectedProgram.program_id);
      if (onProgramsChange) onProgramsChange();
    } catch (error) {
      toast.error("Failed to add scenario");
    }
  };

  // Handle remove scenario
  const handleRemoveScenario = async (programScenarioId) => {
    if (!selectedProgram) return;

    try {
      await removeScenarioFromProgram(
        selectedProgram.program_id,
        programScenarioId,
      );
      toast.success("Scenario removed successfully");
      fetchProgramDetail(selectedProgram.program_id);
      if (onProgramsChange) onProgramsChange();
    } catch (error) {
      toast.error("Failed to remove scenario");
    }
  };

  // Render scenario counts
  const renderCounts = (counts) => (
    <Box className={classes.programCounts}>
      {counts?.actual > 0 && (
        <Chip
          label={`A: ${counts.actual}`}
          size="small"
          className={classes.countChip}
          style={{ backgroundColor: "#4caf50", color: "white" }}
        />
      )}
      {counts?.forecast > 0 && (
        <Chip
          label={`F: ${counts.forecast}`}
          size="small"
          className={classes.countChip}
          style={{ backgroundColor: "#2196f3", color: "white" }}
        />
      )}
      {counts?.policy > 0 && (
        <Chip
          label={`P: ${counts.policy}`}
          size="small"
          className={classes.countChip}
          style={{ backgroundColor: "#ff9800", color: "white" }}
        />
      )}
    </Box>
  );

  // Render list view
  const renderListView = () => (
    <>
      <Box className={classes.listHeader}>
        <Typography variant="subtitle1">
          {programs.length} program{programs.length !== 1 ? "s" : ""}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleCreateNew}
          style={{ backgroundColor: clientColor }}
        >
          New Program
        </Button>
      </Box>

      {isLoading ? (
        <Box className={classes.loadingContainer}>
          <ThreeDots width="60px" color={clientColor} ariaLabel="loading" />
        </Box>
      ) : programs.length === 0 ? (
        <Box className={classes.emptyMessage}>
          <Typography>No programs found. Create your first program!</Typography>
        </Box>
      ) : (
        <List className={classes.programsList}>
          {programs.map((program) => (
            <ListItem
              key={program.program_id}
              className={classes.programItem}
              button
              onClick={() => handleEditProgram(program)}
            >
              <ListItemText
                primary={
                  <Box className={classes.programItemContent}>
                    <Typography className={classes.programName}>
                      {program.program_name}
                    </Typography>
                    {renderCounts(program.scenario_counts)}
                  </Box>
                }
                secondary={
                  <Chip
                    label={program.program_status}
                    size="small"
                    variant="outlined"
                    style={{ marginTop: 4 }}
                  />
                }
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  size="small"
                  onClick={() => handleEditProgram(program)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  edge="end"
                  size="small"
                  onClick={(e) => handleDeleteClick(e, program)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}
    </>
  );

  // Render edit view
  const renderEditView = () => {
    const scenarioTypes = ["actual", "forecast", "policy"];
    const currentType = scenarioTypes[activeTab];
    const currentScenarios = programDetail?.scenarios?.[currentType] || [];

    return (
      <>
        <Box className={classes.editSection}>
          {isLoadingDetail && !isCreating ? (
            <Box className={classes.loadingContainer}>
              <ThreeDots width="60px" color={clientColor} ariaLabel="loading" />
            </Box>
          ) : (
            <>
              {/* Form Fields */}
              <TextField
                label="Program Name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                className={classes.formField}
                required
              />

              <TextField
                label="Description"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                className={classes.formField}
                multiline
                rows={2}
              />

              <FormControl
                variant="outlined"
                size="small"
                className={classes.formField}
                style={{ minWidth: 150 }}
              >
                <InputLabel>Status</InputLabel>
                <Select
                  value={formStatus}
                  onChange={(e) => setFormStatus(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>

              {/* Scenarios Section - only show after program is created */}
              {!isCreating && programDetail && (
                <Box className={classes.scenariosSection}>
                  <Divider style={{ marginBottom: 16 }} />
                  <Typography className={classes.sectionTitle}>
                    Scenarios Assignment
                  </Typography>

                  <Tabs
                    value={activeTab}
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    className={classes.tabsContainer}
                    indicatorColor="primary"
                    textColor="primary"
                  >
                    <Tab
                      label={`Actual (${programDetail?.scenarios?.actual?.length || 0})`}
                    />
                    <Tab
                      label={`Forecast (${programDetail?.scenarios?.forecast?.length || 0})`}
                    />
                    <Tab
                      label={`Policy (${programDetail?.scenarios?.policy?.length || 0})`}
                    />
                  </Tabs>

                  <Box className={classes.tabContent}>
                    {/* Current scenarios */}
                    {currentScenarios.length === 0 ? (
                      <Typography className={classes.emptyMessage}>
                        No scenarios assigned to {currentType}
                      </Typography>
                    ) : (
                      currentScenarios.map((scenario) => (
                        <Box
                          key={scenario.program_scenario_id}
                          className={classes.scenarioRow}
                        >
                          <Typography className={classes.scenarioName}>
                            {scenario.scenario_name ||
                              `Scenario ${scenario.scenario_id}`}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleRemoveScenario(scenario.program_scenario_id)
                            }
                          >
                            <RemoveCircleOutlineIcon
                              fontSize="small"
                              color="error"
                            />
                          </IconButton>
                        </Box>
                      ))
                    )}

                    {/* Add scenario */}
                    {availableScenarios.length > 0 && (
                      <Box className={classes.addScenarioRow}>
                        <FormControl
                          variant="outlined"
                          size="small"
                          className={classes.selectScenario}
                        >
                          <InputLabel>Add Scenario</InputLabel>
                          <Select
                            value={selectedScenarioToAdd}
                            onChange={(e) =>
                              setSelectedScenarioToAdd(e.target.value)
                            }
                            label="Add Scenario"
                          >
                            {availableScenarios.map((scenario) => (
                              <MenuItem
                                key={scenario.scenario_id}
                                value={scenario.scenario_id}
                              >
                                {scenario.scenario_name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleAddScenario}
                          disabled={!selectedScenarioToAdd}
                          startIcon={<AddIcon />}
                        >
                          Add
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Footer Actions */}
        <Box className={classes.actionsFooter}>
          <Button onClick={handleBackToList}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveProgram}
            style={{ backgroundColor: clientColor }}
          >
            {isCreating ? "Create Program" : "Save Changes"}
          </Button>
        </Box>
      </>
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={classes.modalContainer}>
        {/* Header */}
        <Box className={classes.titleSection}>
          <Box className={classes.titleLeft}>
            {view === "edit" && (
              <IconButton size="small" onClick={handleBackToList}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Typography className={classes.title}>
              {view === "list"
                ? "Programs Management"
                : isCreating
                  ? "Create New Program"
                  : `Edit: ${formName || "Program"}`}
            </Typography>
          </Box>
          <IconButton className={classes.closeButton} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box className={classes.contentSection}>
          {view === "list" ? renderListView() : renderEditView()}
        </Box>

        {/* Delete Confirmation Popover */}
        <Popover
          open={Boolean(deleteAnchor)}
          anchorEl={deleteAnchor}
          onClose={handleDeleteCancel}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Box className={classes.popover}>
            <Typography gutterBottom>
              Delete &quot;{programToDelete?.program_name}&quot;?
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              This will remove all scenario associations.
            </Typography>
            <Box display="flex" gap={1} mt={1}>
              <Button size="small" onClick={handleDeleteCancel}>
                Cancel
              </Button>
              <Button
                size="small"
                color="secondary"
                onClick={handleDeleteConfirm}
              >
                Delete
              </Button>
            </Box>
          </Box>
        </Popover>
      </Box>
    </Modal>
  );
}

ModalProgramsManagement.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onProgramsChange: PropTypes.func,
};
